import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { PolicyContent } from "../components/PolicyContent";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { CaretLeft, CaretRight, List, CheckCircle, BookOpen, ChatCircleDots } from "@phosphor-icons/react";

function ChapterList({ chapters, activeId, onPick }) {
  return (
    <nav className="flex flex-col" data-testid="chapter-list">
      {chapters.map((c, i) => (
        <button key={c.rams_id} data-testid={`chapter-${c.rams_id}`} onClick={() => onPick(c.rams_id)}
          className={`text-left px-3 py-2.5 rounded-lg flex items-start gap-2 transition-colors ${c.rams_id === activeId ? "bg-emerald-50 text-emerald-900" : "hover:bg-slate-50 text-slate-700"}`}>
          <span className="font-mono text-xs text-slate-400 mt-0.5 w-5 shrink-0">{i + 1}</span>
          <span className="flex-1 text-sm font-medium leading-snug">{c.title}</span>
          {c.acknowledged ? <CheckCircle size={15} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" /> : c.action_required ? <span className="h-2 w-2 rounded-full bg-amber-400 shrink-0 mt-1.5" /> : null}
        </button>
      ))}
    </nav>
  );
}

export default function BookReaderPage() {
  const { id } = useParams();
  const [col, setCol] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [policy, setPolicy] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get(`/policies/collections/${id}`);
    setCol(r.data);
    const start = r.data.last_rams_id && r.data.chapters.find((c) => c.rams_id === r.data.last_rams_id) ? r.data.last_rams_id : r.data.chapters[0]?.rams_id;
    setActiveId(start);
  }, [id]);
  useEffect(() => { load().catch(() => toast.error("Could not load book")); }, [load]);

  useEffect(() => {
    if (!activeId) return;
    api.get(`/policies/${activeId}`).then((r) => setPolicy(r.data)).catch(() => {});
    api.post(`/policies/collections/${id}/progress`, { last_rams_id: activeId }).catch(() => {});
  }, [activeId, id]);

  if (!col) return <p className="text-slate-400 text-sm">Loading book…</p>;
  const chapters = col.chapters;
  if (!chapters.length) return (
    <div className="max-w-6xl">
      <Link to="/policies" className="text-slate-400 hover:text-slate-700 text-sm">← Library</Link>
      <div className="text-center py-20 text-slate-400" data-testid="book-empty"><p className="font-display text-lg text-slate-500">{col.name}</p><p className="mt-1 text-sm">This book has no documents yet.</p></div>
    </div>
  );
  const idx = chapters.findIndex((c) => c.rams_id === activeId);
  const rev = policy?.current_revision;

  const doAck = async () => {
    try { await api.post(`/policies/${activeId}/acknowledge`); toast.success("Acknowledged"); const r = await api.get(`/policies/${activeId}`, { params: { _t: Date.now() } }); setPolicy(r.data); load(); }
    catch { toast.error("Could not acknowledge"); }
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <Link to="/policies" className="text-slate-400 hover:text-slate-700 text-sm">Library</Link>
          <CaretRight size={14} className="text-slate-300" />
          <h1 className="font-display text-xl font-black text-slate-900 truncate flex items-center gap-2"><BookOpen size={20} weight="bold" className="text-emerald-700" />{col.name}</h1>
        </div>
        {/* Mobile contents */}
        <div className="lg:hidden">
          <Sheet>
            <SheetTrigger asChild><Button data-testid="mobile-contents" variant="outline" size="sm" className="gap-1.5"><List size={16} /> Contents</Button></SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto">
              <SheetHeader><SheetTitle>Contents</SheetTitle></SheetHeader>
              <div className="mt-4"><ChapterList chapters={chapters} activeId={activeId} onPick={setActiveId} /></div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <aside className="hidden lg:block sticky top-6 self-start bg-white border border-slate-200 rounded-xl p-2 max-h-[80vh] overflow-y-auto">
          <ChapterList chapters={chapters} activeId={activeId} onPick={setActiveId} />
        </aside>

        <div>
          {policy ? (
            <article className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="book-page">
              <header className="px-6 sm:px-10 pt-8 pb-5 border-b border-slate-100">
                <div className="font-mono text-xs text-slate-400">Chapter {idx + 1} of {chapters.length} · {policy.ref}{rev ? ` · Rev ${rev.revision}` : ""}</div>
                <h2 className="font-display text-2xl font-black text-slate-900 mt-1">{policy.title}</h2>
              </header>
              <div className="px-6 sm:px-10 py-8"><PolicyContent body={rev?.body} /></div>
              <div className="px-6 sm:px-10 py-4 border-t border-slate-100 flex flex-wrap gap-3 items-center">
                {policy.personal.action_required && <Button data-testid="book-ack" onClick={doAck} className="bg-emerald-700 hover:bg-emerald-800 h-10">{policy.ack_type === "receipt" ? "Confirm receipt" : "Read & acknowledge"}</Button>}
                {!policy.personal.action_required && !policy.personal.acknowledged && policy.personal.ack_message && <span className="text-sm text-slate-500">{policy.personal.ack_message}</span>}
                {policy.personal.acknowledged && <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800"><CheckCircle size={16} weight="fill" /> Acknowledged{rev ? ` · Rev ${rev.revision}` : ""}</span>}
                <Link to={`/policies/${activeId}`} className="text-sm font-semibold text-emerald-700 hover:underline inline-flex items-center gap-1"><ChatCircleDots size={15} /> Open full policy & feedback</Link>
              </div>
            </article>
          ) : <p className="text-slate-400 text-sm">Loading chapter…</p>}

          <div className="flex items-center justify-between mt-4">
            <Button data-testid="prev-chapter" variant="outline" disabled={idx <= 0} onClick={() => setActiveId(chapters[idx - 1]?.rams_id)} className="gap-1"><CaretLeft size={16} /> Previous</Button>
            <span className="text-xs text-slate-400">{idx + 1} / {chapters.length}</span>
            <Button data-testid="next-chapter" variant="outline" disabled={idx >= chapters.length - 1} onClick={() => setActiveId(chapters[idx + 1]?.rams_id)} className="gap-1">Next <CaretRight size={16} /></Button>
          </div>
          <p className="text-center text-xs text-slate-400 mt-3">Reading the whole book does not acknowledge its policies — acknowledge each one individually.</p>
        </div>
      </div>
    </div>
  );
}
